import {
  getCurrentScope, onScopeDispose, ref, watch,
} from 'vue';

/**
 * A value followed live for whatever is shown now, with whether its first answer is still out.
 *
 * ONLY THE LATEST SUBSCRIPTION ANSWERS. Each change of what to follow stops the previous one and
 * starts a numbered one, and an answer from an earlier one is dropped even when it followed the same
 * thing: a card stays mounted as the task it shows changes, so A, then B, then A again is two
 * subscriptions to A, and an answer the first had in flight must not overwrite the second's.
 *
 * Nothing to follow is `null` from `source`: the value goes back to `initial`. A failure logs and
 * leaves what was shown, so the screen keeps what it can without the facts.
 *
 * @template P, T
 * @param {() => P|null} source - what to follow, compared by value to decide whether it changed
 * @param {(params: P, onValue: (value: T) => void, onError: (error: Error) => void) => () => void} subscribe
 * @param {{initial: T, label: string}} options - `label` names what failed to read in the log
 * @returns {{value: import('vue').Ref<T>, reading: import('vue').Ref<boolean>}}
 */
// eslint-disable-next-line import/prefer-default-export
export function useLatestWatch(source, subscribe, { initial, label }) {
  const value = ref(initial);
  const reading = ref(false);
  let latest = 0;
  let stop = () => {};

  watch(() => JSON.stringify(source() ?? null), () => {
    stop();
    stop = () => {};
    latest += 1;
    const request = latest;
    const params = source();

    value.value = initial;
    reading.value = params !== null && params !== undefined;
    if (!reading.value) {
      return;
    }
    stop = subscribe(
      params,
      (answer) => {
        if (request === latest) {
          value.value = answer;
          reading.value = false;
        }
      },
      (error) => {
        console.error(`Failed to read ${label}`, error);
        if (request === latest) {
          reading.value = false;
        }
      },
    );
  }, { immediate: true });

  if (getCurrentScope()) {
    onScopeDispose(() => {
      latest += 1;
      stop();
    });
  }

  return { value, reading };
} // useLatestWatch()
